import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { fade, useTheme } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";
import { Autocomplete } from "@material-ui/lab";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import DeleteIcon from "@material-ui/icons/Delete";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import CloseIcon from "@material-ui/icons/Close";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import Authorizer from "../Authorizer";
import { summarizeValue } from "./CollapsibleValueView";
import { fetchAllApps, selectAllActions, selectApps } from "../../features/allApps/allAppsSlice";
import { createDefaultAction } from "../../features/agents/types";
import { findCatalogAppMeta, getActionSetupIssue } from "../../features/agents/agentSetupIssues";
import { useAgentDynamicGetDD } from "../../features/agents/useAgentDynamicGetDD";
import { ensureBrowserLoginAuth } from "../../features/agents/agentApi";
import {
  WORKLOAD_CODE_PYTHON_STUB,
  findBuiltInActionApp,
  mergeActionAppsWithBuiltIns,
} from "../../constants/builtInActionApps";
import {
  CUSTOM_REQUEST_DEFAULT_VALUE,
  CUSTOM_REQUEST_HTTP_FIELD_NAME,
  appSupportsCustomRequest,
  deriveVerifiedBasesFromActionMap,
  isAbsoluteHttpUrl,
  mergeCustomRequestIntoActionMap,
  normalizeCustomRequestHttpValue,
  resolveAbsoluteApiUrl,
} from "../../constants/customRequestAction";

const parsePerformSpec = (perform = "") => {
  const split = String(perform || "").split(".");
  if (split.length < 6) return null;
  const [app, fieldTitle, action, performDataHead, performKey, performValue] = split;
  return { app, fieldTitle, action, performDataHead, performKey, performValue };
};

const parseDynamicSpec = (dynamic = "") => {
  const split = String(dynamic || "").split(".");
  if (split.length < 6) return null;
  const [app, fieldTitle, action, dataHead, key, value] = split;
  return { app, fieldTitle, action, dataHead, key, value };
};

const withDefaultValue = (field) => {
  if (!field) return field;
  const name = String(field.name || "").toLowerCase();
  if (name === "python_code") {
    if (field.value !== undefined && field.value !== null && String(field.value).trim() !== "") {
      return field;
    }
    return { ...field, value: WORKLOAD_CODE_PYTHON_STUB };
  }
  if (name === "inputs") {
    if (Array.isArray(field.value) && field.value.length > 0) return field;
    const def = field?.schema?.default;
    if (Array.isArray(def) && def.length > 0) return { ...field, value: def };
    return { ...field, value: [{ key: "", value: "Let agent pick value" }] };
  }
  if (name === "http request" || name === CUSTOM_REQUEST_HTTP_FIELD_NAME.toLowerCase()) {
    if (field.value && typeof field.value === "object" && !Array.isArray(field.value)) {
      return {
        ...field,
        value: { ...CUSTOM_REQUEST_DEFAULT_VALUE, ...field.value },
      };
    }
    return { ...field, value: { ...CUSTOM_REQUEST_DEFAULT_VALUE } };
  }
  const hasValue = field.value !== undefined && field.value !== null && field.value !== "";
  if (hasValue) return field;
  return { ...field, value: "Let agent pick value" };
};

/**
 * Ground tool fields on the real Workload action schema (formDataEndpoint).
 * Backfills omitted required fields (e.g. googledrive.uploadFile.file_name) and
 * prefers schema field names/order over invents left on the agent config.
 */
const mergeFieldsWithWorkloadSchema = (existingFields = [], schemaFields = []) => {
  const schema = Array.isArray(schemaFields) ? schemaFields.filter((f) => f && f.name) : [];
  if (schema.length === 0) {
    return Array.isArray(existingFields) ? existingFields.map(withDefaultValue) : [];
  }
  const byName = new Map();
  (existingFields || []).forEach((f) => {
    if (f?.name) byName.set(String(f.name), f);
  });
  const out = schema.map((sf) => {
    const existing = byName.get(sf.name);
    if (existing) {
      return withDefaultValue({
        ...sf,
        ...existing,
        name: sf.name,
        in: existing.in || sf.in,
        schema: existing.schema || sf.schema,
        required: sf.required === true || existing.required === true,
        title: existing.title || sf.title,
        description: existing.description || sf.description,
      });
    }
    return withDefaultValue({ ...sf });
  });
  const schemaNames = new Set(schema.map((f) => f.name));
  (existingFields || []).forEach((f) => {
    const n = String(f?.name || "").toLowerCase();
    if (!f?.name || schemaNames.has(f.name)) return;
    // Keep intentional non-schema fields (Workload Code / Custom Request).
    if (["python_code", "python", "code", "http request", "inputs", "browser task"].includes(n)) {
      out.push(withDefaultValue(f));
    }
  });
  return out;
};

/** True when the field value (or schema) is a structured object/array — not a primitive string. */
const isStructuredFieldValue = (field) => {
  if (!field) return false;
  const schemaType = String(field?.schema?.type || "").toLowerCase();
  if (schemaType === "object" || schemaType === "array") return true;
  const v = field.value;
  if (v == null || v === "" || v === "Let agent pick value") return false;
  if (Array.isArray(v)) return true;
  if (typeof v === "object") return true;
  // Agent sometimes stores objects as JSON strings already
  if (typeof v === "string") {
    const t = v.trim();
    if ((t.startsWith("{") && t.endsWith("}")) || (t.startsWith("[") && t.endsWith("]"))) {
      try {
        const parsed = JSON.parse(t);
        return parsed != null && typeof parsed === "object";
      } catch {
        return false;
      }
    }
  }
  return false;
};

/** Pretty JSON for the editor; never show "[object Object]". */
const formatStructuredValueForEdit = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "string") {
    const t = value.trim();
    if (t === "Let agent pick value") return t;
    if ((t.startsWith("{") && t.endsWith("}")) || (t.startsWith("[") && t.endsWith("]"))) {
      try {
        return JSON.stringify(JSON.parse(t), null, 2);
      } catch {
        return value;
      }
    }
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

/** Parse editor text back to object/array when valid JSON; otherwise keep string. */
const parseStructuredValueFromEdit = (text) => {
  if (text == null) return "Let agent pick value";
  const t = String(text).trim();
  if (!t || t === "Let agent pick value") return "Let agent pick value";
  try {
    return JSON.parse(t);
  } catch {
    return text;
  }
};

/** On save: coerce JSON strings back to objects for known structured fields. */
const coalesceStructuredFieldOnSave = (field) => {
  if (!field || !isStructuredFieldValue(field)) return field;
  if (typeof field.value !== "string") return field;
  const parsed = parseStructuredValueFromEdit(field.value);
  if (parsed !== field.value && typeof parsed === "object") {
    return { ...field, value: parsed };
  }
  return field;
};

const deriveRenderableFields = (fields = []) => {
  const base = Array.isArray(fields) ? fields : [];
  const result = [];
  base.forEach((field) => {
    if (!field || field.hidden === true || field?.schema?.hidden === true) return;
    if (field.name === "body" && field?.schema?.type === "object" && field?.schema?.properties) {
      const bodyRequired = Array.isArray(field.schema.required) ? field.schema.required : [];
      const bodyValue = field.value && typeof field.value === "object" ? field.value : {};
      Object.keys(field.schema.properties).forEach((propertyName) => {
        const propertySchema = field.schema.properties[propertyName] || {};
        if (propertySchema.hidden === true) return;
        result.push({
          __path: `body.${propertyName}`,
          name: propertyName,
          title: propertySchema.title || propertyName,
          required: bodyRequired.includes(propertyName),
          schema: propertySchema,
          value: bodyValue[propertyName] ?? "Let agent pick value",
          parentName: "body",
        });
      });
      return;
    }
    result.push({
      ...field,
      __path: field.name,
      value: field.value ?? "Let agent pick value",
    });
  });
  return result;
};

const toHumanReadableFromKey = (value = "") =>
  String(value || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());

const getActionDisplayLabel = (action = {}) => {
  return (
    action.title ||
    action.label ||
    action.name ||
    action.displayName ||
    (action.key ? toHumanReadableFromKey(action.key) : "")
  );
};

/** Strip leading "AppName: " repeats so display never stacks prefixes. */
const stripLeadingAppPrefix = (text, appName) => {
  let s = String(text || "").trim();
  const app = String(appName || "").trim();
  if (!s || !app) return s;
  const escaped = app.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`^(?:${escaped}\\s*:\\s*)+`, "i");
  return s.replace(re, "").trim() || s;
};

const getConfiguredToolText = (action = {}) => {
  const appName = String(action.app_label || action.app || "Tool").trim() || "Tool";
  // Prefer action_label (raw tool name). `label` is often already "App: Tool".
  const raw =
    action.action_label ||
    action.label ||
    "";
  const actionName = stripLeadingAppPrefix(raw, appName);
  return actionName ? `${appName}: ${actionName}` : appName;
};

const ActionFieldsModal = ({
  open,
  appMeta,
  fields,
  credentialId,
  authName,
  onCredentialChange,
  onAuthNameChange,
  user,
  tenant,
  onClose,
  onSave,
}) => {
  const [localFields, setLocalFields] = useState([]);
  const [showAllFields, setShowAllFields] = useState(false);
  /** Field names whose JSON editor is expanded (collapsed by default). */
  const [expandedStructured, setExpandedStructured] = useState({});


  useEffect(() => {
    const verifiedBase =
      appMeta?.verifiedApiBase || appMeta?.apiBaseUrl || (appMeta?.verifiedBases || [])[0] || "";
    const normalized = Array.isArray(fields)
      ? JSON.parse(JSON.stringify(fields)).map((f) => {
          const withDef = withDefaultValue(f);
          if (
            String(withDef?.name || "").toLowerCase() === "http request" &&
            withDef?.value &&
            typeof withDef.value === "object" &&
            !Array.isArray(withDef.value)
          ) {
            return {
              ...withDef,
              value: normalizeCustomRequestHttpValue(withDef.value, {
                apiBaseUrl: verifiedBase,
                verifiedApiBase: verifiedBase,
                verifiedBases: appMeta?.verifiedBases,
              }),
            };
          }
          return withDef;
        })
      : [];
    setLocalFields(normalized);
  }, [fields, appMeta?.apiBaseUrl, appMeta?.verifiedApiBase, appMeta?.verifiedBases]);

  useEffect(() => {
    if (open) {
      setShowAllFields(false);
      setExpandedStructured({});
    }
  }, [open]);

  // Phase D: ensure live Mongo has username/password authInfo before Sign in popup.
  useEffect(() => {
    if (!open) return;
    const isRpa =
      appMeta?.browserRpa === true ||
      String(appMeta?.automationMode || "").toLowerCase() === "browser_rpa" ||
      String(appMeta?.actionKey || "")
        .toLowerCase()
        .replace(/_/g, "") === "browserrpa";
    const slug = String(appMeta?.app || "").trim();
    if (!isRpa || !slug) return;
    let cancelled = false;
    ensureBrowserLoginAuth({
      app: slug,
      loginUrl: appMeta?.loginUrl || appMeta?.loginRecipe?.loginUrl || "",
    }).then((res) => {
      if (cancelled) return;
      if (res?.ok) {
        console.info(
          "ensureBrowserLoginAuth:",
          res.skipped ? "already configured" : "persisted",
          slug,
        );
      } else {
        console.warn("ensureBrowserLoginAuth failed:", res?.error || res, slug);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open, appMeta?.app, appMeta?.browserRpa, appMeta?.automationMode, appMeta?.actionKey]);

  const { dynamicOptionsByField, dynamicLoadingByField, loadingDynamic } = useAgentDynamicGetDD({
    open,
    appMeta,
    credentialId,
    localFields,
    parseDynamicSpec,
    parsePerformSpec,
  });

  const updateFieldValue = (index, formData) => {
    setLocalFields((prev) => {
      const next = [...prev];
      const target = renderableFields[index];
      if (!target) return prev;
      const safeValue = formData || "Let agent pick value";
      if (target.parentName === "body") {
        const bodyIndex = next.findIndex((field) => field?.name === "body");
        if (bodyIndex === -1) return prev;
        const bodyField = next[bodyIndex];
        const bodyValue = bodyField.value && typeof bodyField.value === "object" ? { ...bodyField.value } : {};
        bodyValue[target.name] = safeValue;
        next[bodyIndex] = { ...bodyField, value: bodyValue };
        return next;
      }
      const fieldIndex = next.findIndex((field) => field?.name === target.name);
      if (fieldIndex === -1) return prev;
      next[fieldIndex] = { ...next[fieldIndex], value: safeValue };
      return next;
    });
  };

  const renderableFields = useMemo(() => deriveRenderableFields(localFields), [localFields]);
  const visibleFields = renderableFields;
  const requiredFields = useMemo(
    () => visibleFields.filter((field) => field?.required === true || field?.schema?.required === true),
    [visibleFields]
  );
  const optionalFields = useMemo(
    () => visibleFields.filter((field) => !(field?.required === true || field?.schema?.required === true)),
    [visibleFields]
  );
  const fieldsToRender = useMemo(() => {
    if (showAllFields) return visibleFields;
    if (requiredFields.length > 0) return requiredFields;
    // If schema does not mark required at this level, don't hide everything.
    return visibleFields;
  }, [showAllFields, visibleFields, requiredFields]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Configure action fields</DialogTitle>
      <DialogContent dividers>
        {(appMeta?.auth === true ||
          appMeta?.needsCredential === true ||
          appMeta?.browserRpa === true ||
          String(appMeta?.automationMode || "").toLowerCase() === "browser_rpa" ||
          String(appMeta?.actionKey || "").toLowerCase().replace(/_/g, "") === "browserrpa" ||
          appSupportsCustomRequest(appMeta) ||
          (Array.isArray(fields) &&
            fields.some((f) => String(f?.name || "").toLowerCase() === "http request"))) ? (
          <Box mb={2}>
            <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
              Connect credential
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 8 }}>
              {appMeta?.browserRpa === true ||
              String(appMeta?.automationMode || "").toLowerCase() === "browser_rpa" ||
              String(appMeta?.actionKey || "").toLowerCase().replace(/_/g, "") === "browserrpa"
                ? "Sign in with the app username and password used for Browser RPA (encrypted). MFA is completed in Preview Live View."
                : "Sign in to save an API key / account, then test the token before running this tool."}
            </Typography>
            <Authorizer
              breakpoint="large"
              user={user}
              tenant={tenant}
              node={{ id: "agent-action-config", data: { authname: authName || "", credential: credentialId || "" } }}
              app={appMeta.app}
              applabel={appMeta.label || appMeta.name}
              icon={appMeta.icon}
              sendData={(id) => onCredentialChange?.(id)}
              sendAuth={(name) => onAuthNameChange?.(name)}
              saveNode={() => {}}
              value={authName || ""}
              sendLoggedOutToAuthorizer={() => {}}
            />
          </Box>
        ) : null}
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
          {String(appMeta?.app || "").toLowerCase() === "workload-code"
            ? "Write stable Python once on this tool. At run time the agent fills Input Data values only — it should not rewrite the code."
            : String(appMeta?.app || "").toLowerCase() === "workload-browser"
            ? "Public-web browsing via AgentCore. The agent fills Session ID and Browser Task each run (navigate, click, type, extract). No connector credential required."
            : (Array.isArray(fields) &&
                fields.some((f) => String(f?.name || "").toLowerCase() === "http request"))
            ? "Custom Request uses this app's connector auth. Prefer a native action when one exists. Use Workload Code for transforms — not this tool."
            : 'Configure fields for this tool. Defaults are set to "Let agent pick value".'}
        </Typography>
        {loadingDynamic ? (
          <Box display="flex" alignItems="center" style={{ marginBottom: 12 }}>
            <CircularProgress size={18} style={{ marginRight: 8 }} />
            <Typography variant="body2" color="textSecondary">
              Loading dynamic options...
            </Typography>
          </Box>
        ) : null}
        {visibleFields.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            This action has no configurable fields.
          </Typography>
        ) : (
          fieldsToRender.map((field) => {
            const index = renderableFields.findIndex((candidate) => candidate?.__path === field?.__path);
            const schema = {
              ...(field.schema || { type: "string" }),
              title: field.title || field.name || `Field ${index + 1}`,
            };
            const dynamicOptions = dynamicOptionsByField[field?.name] || [];
            const dynamicLoading = Boolean(dynamicLoadingByField[field?.name]);
            const isDynamic = Boolean(schema.dynamic);
            const currentValue = field?.value;
            const selectedDynamicValue =
              currentValue && typeof currentValue === "object"
                ? currentValue?.value ?? currentValue?.id ?? "Let agent pick value"
                : currentValue ?? "Let agent pick value";
            const isBoolean = schema.type === "boolean";
            const hasEnum = Array.isArray(schema.enum) && schema.enum.length > 0;
            const enumNames = Array.isArray(schema.enumNames) ? schema.enumNames : [];
            const isPythonCode = String(field?.name || "").toLowerCase() === "python_code";
            const isInputsArray =
              String(field?.name || "").toLowerCase() === "inputs" && schema.type === "array";
            const isHttpRequest =
              String(field?.name || "").toLowerCase() === "http request" &&
              (schema.type === "object" || (field.value && typeof field.value === "object"));
            const httpValue =
              field.value && typeof field.value === "object" && !Array.isArray(field.value)
                ? { ...CUSTOM_REQUEST_DEFAULT_VALUE, ...field.value }
                : { ...CUSTOM_REQUEST_DEFAULT_VALUE };
            const httpParams = Array.isArray(httpValue.params) ? httpValue.params : [{ key: "", value: "" }];
            const httpHeaders = Array.isArray(httpValue.headers) ? httpValue.headers : [{ key: "", value: "" }];
            const inputsRows = Array.isArray(field.value)
              ? field.value
              : [{ key: "", value: "Let agent pick value" }];

            return (
              <Box key={field.name || index} mb={2}>
                {isDynamic ? (
                  <TextField
                    select
                    fullWidth
                    variant="outlined"
                    label={schema.title}
                    value={selectedDynamicValue}
                    onChange={(event) => {
                      if (event.target.value === "Let agent pick value") {
                        updateFieldValue(index, "Let agent pick value");
                        return;
                      }
                      const selected = dynamicOptions.find((item) => String(item.id) === String(event.target.value));
                      updateFieldValue(index, selected ? { label: selected.name, value: selected.id } : event.target.value);
                    }}
                    helperText={dynamicLoading ? "Loading options..." : schema.description || ""}
                    disabled={dynamicLoading || !credentialId}
                  >
                    <MenuItem value="Let agent pick value">Let agent pick value</MenuItem>
                    {dynamicOptions.map((option) => (
                      <MenuItem key={`${field.name}_${option.id}`} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : isBoolean ? (
                  <TextField
                    select
                    fullWidth
                    variant="outlined"
                    label={schema.title}
                    value={selectedDynamicValue}
                    onChange={(event) => {
                      if (event.target.value === "Let agent pick value") {
                        updateFieldValue(index, "Let agent pick value");
                        return;
                      }
                      updateFieldValue(index, event.target.value === "true");
                    }}
                    helperText={schema.description || ""}
                  >
                    <MenuItem value="Let agent pick value">Let agent pick value</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </TextField>
                ) : hasEnum ? (
                  <TextField
                    select
                    fullWidth
                    variant="outlined"
                    label={schema.title}
                    value={selectedDynamicValue}
                    onChange={(event) => updateFieldValue(index, event.target.value)}
                    helperText={schema.description || ""}
                  >
                    <MenuItem value="Let agent pick value">Let agent pick value</MenuItem>
                    {schema.enum.map((enumValue, enumIndex) => (
                      <MenuItem key={`${field.name}_${String(enumValue)}_${enumIndex}`} value={enumValue}>
                        {enumNames[enumIndex] || String(enumValue)}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : isHttpRequest ? (
                  <Box>
                    <Typography variant="subtitle2" style={{ marginBottom: 6 }}>
                      {schema.title || CUSTOM_REQUEST_HTTP_FIELD_NAME}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 8 }}>
                      {schema.description ||
                        "Method, URL, optional params/headers/body. Connector auth is applied automatically."}
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      variant="outlined"
                      label="Request Type"
                      value={httpValue.requestType || "GET"}
                      onChange={(event) =>
                        updateFieldValue(index, { ...httpValue, requestType: event.target.value })
                      }
                      style={{ marginBottom: 8 }}
                    >
                      {["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => (
                        <MenuItem key={m} value={m}>
                          {m}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="URL"
                      value={httpValue.url || ""}
                      onChange={(event) => updateFieldValue(index, { ...httpValue, url: event.target.value })}
                      onBlur={() => {
                        const verifiedBase =
                          appMeta?.verifiedApiBase ||
                          appMeta?.apiBaseUrl ||
                          (appMeta?.verifiedBases || [])[0] ||
                          "";
                        const nextUrl = resolveAbsoluteApiUrl(httpValue.url, {
                          apiBaseUrl: verifiedBase,
                          verifiedApiBase: verifiedBase,
                          verifiedBases: appMeta?.verifiedBases,
                        });
                        if (nextUrl && nextUrl !== httpValue.url && isAbsoluteHttpUrl(nextUrl)) {
                          updateFieldValue(index, { ...httpValue, url: nextUrl });
                        }
                      }}
                      error={Boolean(
                        httpValue.url &&
                          String(httpValue.url).trim() &&
                          String(httpValue.url).trim() !== "Let agent pick value" &&
                          !isAbsoluteHttpUrl(httpValue.url)
                      )}
                      helperText={
                        isAbsoluteHttpUrl(httpValue.url)
                          ? "Full endpoint — ready to run."
                          : appMeta?.verifiedApiBase || appMeta?.apiBaseUrl
                            ? `Enter a path or full URL. Verified base: ${
                                appMeta.verifiedApiBase || appMeta.apiBaseUrl
                              }`
                            : "Paste the full API URL from the docs (https://host/path). We only auto-fill when a verified base exists from sibling actions or docs ingest."
                      }
                      style={{ marginBottom: 8 }}
                    />
                    {appMeta?.apiDocs ? (
                      <Typography variant="caption" display="block" style={{ marginBottom: 8 }}>
                        <a href={String(appMeta.apiDocs)} target="_blank" rel="noopener noreferrer">
                          Open API docs
                        </a>
                      </Typography>
                    ) : null}
                    <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 4 }}>
                      URL Parameters
                    </Typography>
                    {httpParams.map((row, rowIdx) => (
                      <Box key={`http_params_${rowIdx}`} display="flex" style={{ gap: 8, marginBottom: 8 }}>
                        <TextField
                          size="small"
                          variant="outlined"
                          label="Key"
                          value={row?.key ?? ""}
                          onChange={(event) => {
                            const next = httpParams.map((r, i) =>
                              i === rowIdx ? { ...r, key: event.target.value } : r
                            );
                            updateFieldValue(index, { ...httpValue, params: next });
                          }}
                          style={{ flex: "0 0 28%" }}
                        />
                        <TextField
                          size="small"
                          variant="outlined"
                          label="Value"
                          value={row?.value ?? ""}
                          onChange={(event) => {
                            const next = httpParams.map((r, i) =>
                              i === rowIdx ? { ...r, value: event.target.value } : r
                            );
                            updateFieldValue(index, { ...httpValue, params: next });
                          }}
                          style={{ flex: 1 }}
                        />
                        <Button
                          size="small"
                          onClick={() => {
                            const next = httpParams.filter((_, i) => i !== rowIdx);
                            updateFieldValue(index, {
                              ...httpValue,
                              params: next.length ? next : [{ key: "", value: "" }],
                            });
                          }}
                        >
                          Remove
                        </Button>
                      </Box>
                    ))}
                    <Button
                      size="small"
                      variant="outlined"
                      style={{ marginBottom: 12 }}
                      onClick={() =>
                        updateFieldValue(index, {
                          ...httpValue,
                          params: [...httpParams, { key: "", value: "Let agent pick value" }],
                        })
                      }
                    >
                      + Add param
                    </Button>
                    <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 4 }}>
                      Headers (optional — skip auth headers)
                    </Typography>
                    {httpHeaders.map((row, rowIdx) => (
                      <Box key={`http_headers_${rowIdx}`} display="flex" style={{ gap: 8, marginBottom: 8 }}>
                        <TextField
                          size="small"
                          variant="outlined"
                          label="Key"
                          value={row?.key ?? ""}
                          onChange={(event) => {
                            const next = httpHeaders.map((r, i) =>
                              i === rowIdx ? { ...r, key: event.target.value } : r
                            );
                            updateFieldValue(index, { ...httpValue, headers: next });
                          }}
                          style={{ flex: "0 0 28%" }}
                        />
                        <TextField
                          size="small"
                          variant="outlined"
                          label="Value"
                          value={row?.value ?? ""}
                          onChange={(event) => {
                            const next = httpHeaders.map((r, i) =>
                              i === rowIdx ? { ...r, value: event.target.value } : r
                            );
                            updateFieldValue(index, { ...httpValue, headers: next });
                          }}
                          style={{ flex: 1 }}
                        />
                        <Button
                          size="small"
                          onClick={() => {
                            const next = httpHeaders.filter((_, i) => i !== rowIdx);
                            updateFieldValue(index, {
                              ...httpValue,
                              headers: next.length ? next : [{ key: "", value: "" }],
                            });
                          }}
                        >
                          Remove
                        </Button>
                      </Box>
                    ))}
                    <Button
                      size="small"
                      variant="outlined"
                      style={{ marginBottom: 12 }}
                      onClick={() =>
                        updateFieldValue(index, {
                          ...httpValue,
                          headers: [...httpHeaders, { key: "", value: "" }],
                        })
                      }
                    >
                      + Add header
                    </Button>
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      rowsMax={16}
                      variant="outlined"
                      label="JSON Body"
                      value={httpValue.stringBody || ""}
                      onChange={(event) =>
                        updateFieldValue(index, { ...httpValue, stringBody: event.target.value })
                      }
                      helperText="Ignored for GET. JSON text; leave dynamic slots for the agent when needed."
                      disabled={String(httpValue.requestType || "GET").toUpperCase() === "GET"}
                      InputProps={{
                        style: {
                          fontFamily:
                            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                          fontSize: 13,
                        },
                      }}
                    />
                  </Box>
                ) : isInputsArray ? (
                  <Box>
                    <Typography variant="subtitle2" style={{ marginBottom: 6 }}>
                      {schema.title || "Input Data"}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 8 }}>
                      {schema.description || "Keys become body / input_data in Python. Values can stay “Let agent pick value”."}
                    </Typography>
                    {inputsRows.map((row, rowIdx) => (
                      <Box key={`inputs_${rowIdx}`} display="flex" style={{ gap: 8, marginBottom: 8 }}>
                        <TextField
                          size="small"
                          variant="outlined"
                          label="Key"
                          value={row?.key ?? ""}
                          onChange={(event) => {
                            const next = inputsRows.map((r, i) =>
                              i === rowIdx ? { ...r, key: event.target.value } : r
                            );
                            updateFieldValue(index, next);
                          }}
                          style={{ flex: "0 0 28%" }}
                        />
                        <TextField
                          size="small"
                          variant="outlined"
                          label="Value"
                          value={row?.value ?? ""}
                          onChange={(event) => {
                            const next = inputsRows.map((r, i) =>
                              i === rowIdx ? { ...r, value: event.target.value } : r
                            );
                            updateFieldValue(index, next);
                          }}
                          style={{ flex: 1 }}
                        />
                        <Button
                          size="small"
                          onClick={() => {
                            const next = inputsRows.filter((_, i) => i !== rowIdx);
                            updateFieldValue(index, next.length ? next : [{ key: "", value: "Let agent pick value" }]);
                          }}
                        >
                          Remove
                        </Button>
                      </Box>
                    ))}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        updateFieldValue(index, [...inputsRows, { key: "", value: "Let agent pick value" }])
                      }
                    >
                      + Add input
                    </Button>
                  </Box>
                ) : isPythonCode ? (
                  <TextField
                    fullWidth
                    multiline
                    minRows={10}
                    rowsMax={24}
                    variant="outlined"
                    label={schema.title || "Python Code"}
                    value={typeof field.value === "string" ? field.value : String(field.value ?? "")}
                    onChange={(event) => updateFieldValue(index, event.target.value)}
                    helperText={schema.description || ""}
                    InputProps={{
                      style: {
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                        fontSize: 13,
                      },
                    }}
                    inputProps={{
                      style: {
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                        fontSize: 13,
                        lineHeight: 1.45,
                      },
                    }}
                  />
                ) : isStructuredFieldValue(field) ? (
                  <Box
                    style={{
                      border: "1px solid rgba(0,0,0,0.12)",
                      borderRadius: 6,
                      padding: 10,
                      backgroundColor: "rgba(0,0,0,0.02)",
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="flex-start"
                      justifyContent="space-between"
                      style={{ gap: 8 }}
                    >
                      <Box style={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle2" style={{ marginBottom: 2 }}>
                          {schema.title || field.name || "JSON"}
                        </Typography>
                        {!expandedStructured[field.name || index] ? (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ wordBreak: "break-word" }}
                          >
                            {summarizeValue(
                              typeof field.value === "string"
                                ? (() => {
                                    try {
                                      return JSON.parse(field.value);
                                    } catch {
                                      return field.value;
                                    }
                                  })()
                                : field.value,
                            )}
                          </Typography>
                        ) : null}
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          setExpandedStructured((prev) => ({
                            ...prev,
                            [field.name || index]: !prev[field.name || index],
                          }))
                        }
                        style={{ flexShrink: 0 }}
                      >
                        {expandedStructured[field.name || index] ? "Collapse" : "Edit JSON"}
                      </Button>
                    </Box>
                    {expandedStructured[field.name || index] ? (
                      <Box mt={1.5}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={6}
                          rowsMax={24}
                          variant="outlined"
                          label="Edit as JSON"
                          value={formatStructuredValueForEdit(field.value)}
                          onChange={(event) => updateFieldValue(index, event.target.value)}
                          onBlur={(event) => {
                            const parsed = parseStructuredValueFromEdit(event.target.value);
                            updateFieldValue(index, parsed);
                          }}
                          helperText={
                            schema.description ||
                            "Edit JSON below; it is validated when you leave the field."
                          }
                          InputProps={{
                            style: {
                              fontFamily:
                                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                              fontSize: 13,
                            },
                          }}
                          inputProps={{
                            style: {
                              fontFamily:
                                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                              fontSize: 13,
                              lineHeight: 1.45,
                            },
                          }}
                        />
                      </Box>
                    ) : null}
                  </Box>
                ) : (
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={schema.title}
                    type={schema.type === "number" || schema.type === "integer" ? "number" : "text"}
                    value={
                      field.value == null || typeof field.value === "object"
                        ? "Let agent pick value"
                        : field.value
                    }
                    onChange={(event) => updateFieldValue(index, event.target.value)}
                    helperText={schema.description || ""}
                  />
                )}
              </Box>
            );
          })
        )}
        {requiredFields.length > 0 && optionalFields.length > 0 ? (
          <Box mt={1}>
            <Button size="small" onClick={() => setShowAllFields((prev) => !prev)}>
              {showAllFields ? "Hide optional fields" : `Show more fields (${optionalFields.length})`}
            </Button>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="primary"
          variant="contained"
          disabled={Boolean(
            (localFields || []).some((f) => {
              if (String(f?.name || "").toLowerCase() !== "http request") return false;
              const url = f?.value && typeof f.value === "object" ? f.value.url : "";
              return !url || !isAbsoluteHttpUrl(url);
            })
          )}
          onClick={() => onSave(localFields)}
        >
          Save action fields
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AgentActionsSection = ({ actions = [], onChange, onCommit = null }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const actionAppsFromCatalog = useSelector(selectAllActions);
  const allAppsCatalog = useSelector(selectApps);
  const actionApps = useMemo(
    () => mergeActionAppsWithBuiltIns(actionAppsFromCatalog || []),
    [actionAppsFromCatalog]
  );
  const appsLoading = useSelector((state) => state.allApps?.loading);
  const actionsSectionRef = useRef(null);
  const [setupGuideAlert, setSetupGuideAlert] = useState(null);

  const [setupOpen, setSetupOpen] = useState(false);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [draft, setDraft] = useState(createDefaultAction());
  const [actionChoices, setActionChoices] = useState([]);
  const [verifiedBasesFromActions, setVerifiedBasesFromActions] = useState([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuIndex, setMenuIndex] = useState(-1);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState(-1);

  const hasActions = useMemo(() => Array.isArray(actions) && actions.length > 0, [actions]);
  const catalogReady = useMemo(
    () =>
      appsLoading === "loaded" ||
      appsLoading === "failedLoad" ||
      (appsLoading !== "loading" && (allAppsCatalog || []).length > 0),
    [appsLoading, allAppsCatalog]
  );
  const selectedActionApp = useMemo(() => {
    const fromMerged = (actionApps || []).find(
      (item) => String(item?.app || item?.slug || "").toLowerCase() === String(draft.app || "").toLowerCase()
    );
    return fromMerged || findCatalogAppMeta(allAppsCatalog || [], draft.app) || findBuiltInActionApp(draft.app);
  }, [actionApps, allAppsCatalog, draft.app]);
  const selectedActionChoice = useMemo(
    () => (actionChoices || []).find((item) => item.key === draft.action_key) || null,
    [actionChoices, draft.action_key]
  );

  // When editing an existing tool, re-ground fields on Workload formData so required
  // schema fields the Copilot omitted (e.g. uploadFile.file_name) appear in Configure.
  useEffect(() => {
    if (!fieldsOpen || !selectedActionChoice) return;
    const schemaFields = selectedActionChoice.fields || [];
    if (!Array.isArray(schemaFields) || schemaFields.length === 0) return;
    setDraft((prev) => {
      const merged = mergeFieldsWithWorkloadSchema(prev.fields || [], schemaFields);
      const prevNames = (prev.fields || []).map((f) => f?.name).filter(Boolean).join("|");
      const nextNames = merged.map((f) => f?.name).filter(Boolean).join("|");
      if (prevNames === nextNames && (prev.fields || []).length === merged.length) {
        // Still refresh required/schema metadata even if names match
        const sameMeta = (prev.fields || []).every((f, i) => {
          const m = merged[i];
          return (
            f?.name === m?.name &&
            Boolean(f?.required) === Boolean(m?.required) &&
            (f?.schema ? true : !m?.schema)
          );
        });
        if (sameMeta) return prev;
      }
      return { ...prev, fields: merged };
    });
  }, [fieldsOpen, selectedActionChoice]);

  useEffect(() => {
    if (!allAppsCatalog || allAppsCatalog.length === 0) {
      dispatch(fetchAllApps(1));
    }
  }, [dispatch, allAppsCatalog]);

  useEffect(() => {
    let cancelled = false;
    const mapActionChoices = (actionMap) =>
      Object.keys(actionMap || {}).map((key) => ({
        key,
        label: getActionDisplayLabel({ key, ...(actionMap[key] || {}) }) || key,
        ...actionMap[key],
      }));

    const loadActions = async () => {
      if (!selectedActionApp) {
        setActionChoices([]);
        setVerifiedBasesFromActions([]);
        return;
      }
      const mergeChoices = (actionMap) =>
        mapActionChoices(mergeCustomRequestIntoActionMap(actionMap, selectedActionApp));
      const rememberBases = (actionMap) => {
        const derived = deriveVerifiedBasesFromActionMap(actionMap);
        setVerifiedBasesFromActions(derived.bases || []);
      };

      const inlineMap = selectedActionApp.actions;
      if (inlineMap && typeof inlineMap === "object" && Object.keys(inlineMap).length > 0) {
        setActionChoices(mergeChoices(inlineMap));
        rememberBases(inlineMap);
      } else if (appSupportsCustomRequest(selectedActionApp)) {
        setActionChoices(mergeChoices({}));
        setVerifiedBasesFromActions([]);
      }
      if (!selectedActionApp.formDataEndpoint) {
        setActionsLoading(false);
        return;
      }
      setActionsLoading(true);
      try {
        const response = await axios.get(selectedActionApp.formDataEndpoint);
        const actionMap = response?.data?.actions || {};
        const mapped = mergeChoices(actionMap);
        if (!cancelled && mapped.length > 0) {
          setActionChoices(mapped);
          rememberBases(actionMap);
        }
      } catch (err) {
        // Keep inline / Custom Request choices when GET fails.
        if (!cancelled && !(inlineMap && Object.keys(inlineMap).length > 0)) {
          if (appSupportsCustomRequest(selectedActionApp)) {
            setActionChoices(mergeChoices({}));
          } else {
            setActionChoices([]);
          }
        }
      } finally {
        if (!cancelled) setActionsLoading(false);
      }
    };
    loadActions();
    return () => {
      cancelled = true;
    };
  }, [selectedActionApp]);

  const removeAt = (index) => onChange((actions || []).filter((_, idx) => idx !== index));

  const openAddTool = () => {
    setSetupGuideAlert(null);
    setEditingIndex(-1);
    setDraft({
      ...createDefaultAction(),
      label: "",
      type: "run_workflow_step",
      app: "",
      action_key: "",
      action_label: "",
      app_label: "",
      app_icon: "",
      url: "",
      fields: [],
      credential: "",
      authname: "",
      config: {},
    });
    setActionChoices([]);
    setSetupOpen(true);
  };

  const openEditTool = (index, issue = null) => {
    const action = actions[index];
    if (!action) return;
    setEditingIndex(index);
    setDraft({
      ...action,
    });
    if (issue) {
      setSetupGuideAlert({ title: issue.alertTitle, body: issue.alertBody });
      actionsSectionRef.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
    } else {
      setSetupGuideAlert(null);
    }
    if (action.app && action.action_key) {
      setSetupOpen(false);
      setFieldsOpen(true);
    } else {
      setSetupOpen(true);
      setFieldsOpen(false);
    }
  };

  const persistTool = (fieldsOverride = null, draftOverride = null) => {
    const baseDraft = draftOverride || draft;
    const nextFields = (Array.isArray(fieldsOverride) ? fieldsOverride : baseDraft.fields || []).map(withDefaultValue);
    const appName = String(baseDraft.app_label || baseDraft.app || "").trim();
    const cleanActionName =
      stripLeadingAppPrefix(baseDraft.action_label || baseDraft.label || "", appName) ||
      baseDraft.action_label ||
      "Configured tool";
    const nextAction = {
      ...baseDraft,
      action_label: cleanActionName,
      label: appName ? `${appName}: ${cleanActionName}` : cleanActionName,
      type: "run_workflow_step",
      fields: nextFields,
      config: {
        ...(baseDraft.config && typeof baseDraft.config === "object" ? baseDraft.config : {}),
        app: baseDraft.app,
        action: baseDraft.action_key,
        credential: baseDraft.credential || "",
        authname: baseDraft.authname || "",
        fields: nextFields,
        ...(baseDraft.verifiedApiBase || baseDraft.apiBaseUrl
          ? {
              verifiedApiBase: baseDraft.verifiedApiBase || baseDraft.apiBaseUrl,
              apiBaseUrl: baseDraft.apiBaseUrl || baseDraft.verifiedApiBase,
            }
          : {}),
        ...(baseDraft.apiDocs ? { apiDocs: baseDraft.apiDocs } : {}),
      },
    };
    const next =
      editingIndex >= 0
        ? (actions || []).map((item, idx) => (idx === editingIndex ? { ...item, ...nextAction } : item))
        : [...(actions || []), nextAction];
    onChange(next);
    return next;
  };

  const saveSetupAndMaybeConfigure = () => {
    if (!draft.app || !draft.action_key) return;
    setSetupOpen(false);
    setFieldsOpen(true);
  };

  return (
    <Card ref={actionsSectionRef} id="agent-actions-section">
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6">Tools this agent can use</Typography>
          {hasActions ? (
            <Button size="small" variant="outlined" onClick={openAddTool}>
              + Add tool
            </Button>
          ) : null}
        </Box>
        {setupGuideAlert ? (
          <Box mb={2}>
            <Alert severity="error" onClose={() => setSetupGuideAlert(null)}>
              <Typography variant="subtitle2" gutterBottom>
                {setupGuideAlert.title}
              </Typography>
              <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                {setupGuideAlert.body}
              </Typography>
            </Alert>
          </Box>
        ) : null}
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
          Pick the actions the agent is allowed to execute. Use Workload Code for stable Python
          transforms — the model fills inputs each run, not the code. Use Custom Request only when
          an app has connector auth and API docs but no native action for what you need.
        </Typography>
        {!hasActions ? (
          <Paper variant="outlined" style={{ padding: 12 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="body1">+ Add a tool this agent can use...</Typography>
              <Button size="small" variant="outlined" onClick={openAddTool}>
                + Add tool
              </Button>
            </Box>
          </Paper>
        ) : (
          (actions || []).map((action, index) => {
            const appMeta = findCatalogAppMeta(allAppsCatalog || [], action.app);
            const actionIssue = getActionSetupIssue(action, appMeta, { catalogReady });
            return (
              <Paper
                key={action.action_id || index}
                variant="outlined"
                onClick={actionIssue ? () => openEditTool(index, actionIssue) : undefined}
                style={{
                  padding: 10,
                  marginBottom: 8,
                  cursor: actionIssue ? "pointer" : "default",
                  backgroundColor: actionIssue ? fade(theme.palette.error.main, 0.08) : undefined,
                  borderColor: actionIssue ? theme.palette.error.main : undefined,
                  borderWidth: actionIssue ? 1 : undefined,
                }}
              >
                <Box display="flex" alignItems="center" flexWrap="nowrap" style={{ gap: 8, minWidth: 0 }}>
                  <Box display="flex" alignItems="center" minWidth={0} flex={1} style={{ minWidth: 0 }}>
                    {action.app_icon ? (
                      <Avatar src={action.app_icon} style={{ marginRight: 8, flexShrink: 0 }} />
                    ) : null}
                    <Typography variant="body1" noWrap style={{ minWidth: 0 }}>
                      {getConfiguredToolText(action)}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" style={{ gap: 8, flexShrink: 0, marginLeft: "auto" }}>
                    {actionIssue ? (
                      <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{actionIssue.tooltip}</span>}>
                        <Chip
                          size="small"
                          label="Setup issue"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            backgroundColor: fade(theme.palette.error.main, 0.18),
                            color: theme.palette.error.dark,
                            fontWeight: 600,
                          }}
                        />
                      </Tooltip>
                    ) : null}
                    <IconButton
                      aria-label="More actions"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuAnchorEl(e.currentTarget);
                        setMenuIndex(index);
                      }}
                      size="small"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            );
          })
        )}

        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={() => setMenuAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() => {
              setMenuAnchorEl(null);
              openEditTool(menuIndex, null);
            }}
          >
            <ListItemIcon>
              <EditOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Edit" />
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchorEl(null);
              setDeleteConfirmIndex(menuIndex);
              setDeleteConfirmOpen(true);
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Remove from agent" />
          </MenuItem>
        </Menu>

        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">Remove from agent?</Typography>
              <IconButton size="small" aria-label="Close" onClick={() => setDeleteConfirmOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" color="textPrimary">
              Are you sure you want to remove this tool from the agent?
            </Typography>
          </DialogContent>
          <DialogActions style={{ padding: "8px 24px 16px" }}>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => {
                if (deleteConfirmIndex >= 0) {
                  removeAt(deleteConfirmIndex);
                }
                setDeleteConfirmOpen(false);
                setDeleteConfirmIndex(-1);
              }}
              startIcon={<DeleteIcon />}
              style={{ backgroundColor: "#d32f2f", color: "#fff" }}
            >
              Remove from agent
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={setupOpen}
          onClose={() => {
            setSetupGuideAlert(null);
            setSetupOpen(false);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{editingIndex >= 0 ? "Edit tool" : "Add tool"}</DialogTitle>
          <DialogContent dividers>
            <Box mb={2}>
              <Autocomplete
                options={actionApps || []}
                loading={appsLoading === "loading"}
                value={selectedActionApp}
                getOptionLabel={(option) => option?.name || ""}
                renderOption={(option) => (
                  <Box display="flex" alignItems="center">
                    <Avatar src={option?.icon} style={{ width: 22, height: 22, marginRight: 8 }} />
                    <Typography variant="body2">{option?.name || ""}</Typography>
                  </Box>
                )}
                onChange={(_, value) => {
                  if (!value) {
                    setDraft((prev) => ({
                      ...prev,
                      app: "",
                      action_key: "",
                      action_label: "",
                      app_label: "",
                      app_icon: "",
                      url: "",
                      fields: [],
                      credential: "",
                      authname: "",
                    }));
                    setActionChoices([]);
                    return;
                  }
                  setDraft((prev) => ({
                    ...prev,
                    app: value.app,
                    action_key: "",
                    action_label: "",
                    app_label: value.name,
                    app_icon: value.icon,
                    url: value.formDataEndpoint,
                    fields: [],
                    credential: prev.app === value.app ? prev.credential : "",
                    authname: prev.app === value.app ? prev.authname : "",
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Action app"
                    variant="outlined"
                    placeholder="Search action apps..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: selectedActionApp?.icon ? (
                        <>
                          <Avatar src={selectedActionApp.icon} style={{ width: 22, height: 22, marginRight: 8 }} />
                          {params.InputProps.startAdornment}
                        </>
                      ) : (
                        params.InputProps.startAdornment
                      ),
                    }}
                  />
                )}
              />
            </Box>
            {selectedActionApp ? (
              <Box mb={1}>
                <Autocomplete
                  options={actionChoices || []}
                  loading={actionsLoading}
                  value={selectedActionChoice}
                  getOptionLabel={(option) => option?.label || option?.key || ""}
                  onChange={(_, value) => {
                    if (!value) {
                      setDraft((prev) => ({
                        ...prev,
                        action_key: "",
                        action_label: "",
                        fields: [],
                      }));
                      return;
                    }
                    const existingByName = new Map((draft.fields || []).map((field) => [field?.name, field]));
                    const mergedFields = mergeFieldsWithWorkloadSchema(
                      draft.fields || [],
                      value.fields || []
                    );
                    // Preserve values already set for matching names
                    const withValues = mergedFields.map((field) => {
                      const existing = existingByName.get(field?.name);
                      return existing ? { ...field, value: existing.value } : field;
                    });
                    setDraft((prev) => ({
                      ...prev,
                      action_key: value.key,
                      action_label: getActionDisplayLabel(value) || value.key,
                      fields: withValues,
                    }));
                  }}
                  renderOption={(option) => (
                    <Box display="flex" alignItems="flex-start">
                      {selectedActionApp?.icon ? (
                        <Avatar src={selectedActionApp.icon} style={{ width: 22, height: 22, marginRight: 8, marginTop: 2 }} />
                      ) : null}
                      <Box>
                        <Typography variant="body1" style={{ fontWeight: 700 }}>
                          {getActionDisplayLabel(option) || option?.key || ""}
                        </Typography>
                        {option?.description ? (
                          <Typography variant="body2" color="textSecondary">
                            {option.description}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Action"
                      variant="outlined"
                      placeholder="Select an action..."
                      helperText={selectedActionApp?.name ? `Search for ${selectedActionApp.name} actions` : ""}
                    />
                  )}
                />
              </Box>
            ) : null}
            <FormHelperText style={{ marginTop: 8 }}>
              Action fields default to &quot;Let agent pick value&quot; in the next step.
            </FormHelperText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setSetupGuideAlert(null);
                setSetupOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              disabled={!draft.app || !draft.action_key}
              onClick={saveSetupAndMaybeConfigure}
            >
              {draft.fields && draft.fields.length > 0 ? "Next: configure fields" : "Save tool"}
            </Button>
          </DialogActions>
        </Dialog>

        <ActionFieldsModal
          open={fieldsOpen}
          appMeta={
            selectedActionApp
              ? {
                  url: selectedActionApp.formDataEndpoint,
                  app: selectedActionApp.app,
                  label: selectedActionApp.name,
                  icon: selectedActionApp.icon,
                  auth:
                    selectedActionApp.auth === true ||
                    draft.needsCredential === true ||
                    draft.config?.needsCredential === true ||
                    draft.config?.authDrafted === true ||
                    draft.auth === true ||
                    String(draft.action_key || draft.config?.action || "")
                      .toLowerCase()
                      .replace(/_/g, "") === "browserrpa" ||
                    selectedActionApp.browserRpa === true ||
                    String(selectedActionApp.automationMode || "").toLowerCase() === "browser_rpa",
                  needsCredential:
                    draft.needsCredential === true ||
                    draft.config?.needsCredential === true ||
                    draft.config?.authDrafted === true ||
                    String(draft.action_key || draft.config?.action || "")
                      .toLowerCase()
                      .replace(/_/g, "") === "browserrpa",
                  browserRpa:
                    selectedActionApp.browserRpa === true ||
                    draft.config?.browserRpa === true ||
                    String(selectedActionApp.automationMode || draft.config?.automationMode || "")
                      .toLowerCase() === "browser_rpa" ||
                    String(draft.action_key || draft.config?.action || "")
                      .toLowerCase()
                      .replace(/_/g, "") === "browserrpa",
                  automationMode:
                    selectedActionApp.automationMode ||
                    draft.config?.automationMode ||
                    (String(draft.action_key || "")
                      .toLowerCase()
                      .replace(/_/g, "") === "browserrpa"
                      ? "browser_rpa"
                      : ""),
                  actionKey: draft.action_key || draft.config?.action || "",
                  apiDocs:
                    draft.apiDocs ||
                    draft.config?.apiDocs ||
                    selectedActionApp.apiDocs ||
                    selectedActionApp.apiDocsUrl ||
                    "",
                  apiDocsUrl:
                    draft.config?.apiDocs ||
                    selectedActionApp.apiDocsUrl ||
                    selectedActionApp.apiDocs ||
                    "",
                  verifiedBases: verifiedBasesFromActions,
                  verifiedApiBase:
                    draft.verifiedApiBase ||
                    draft.apiBaseUrl ||
                    draft.config?.verifiedApiBase ||
                    draft.config?.apiBaseUrl ||
                    verifiedBasesFromActions[0] ||
                    "",
                  apiBaseUrl:
                    draft.apiBaseUrl ||
                    draft.verifiedApiBase ||
                    draft.config?.apiBaseUrl ||
                    draft.config?.verifiedApiBase ||
                    verifiedBasesFromActions[0] ||
                    "",
                }
              : null
          }
          fields={draft.fields || []}
          credentialId={draft.credential || null}
          authName={draft.authname || ""}
          onCredentialChange={(credential) => setDraft((prev) => ({ ...prev, credential }))}
          onAuthNameChange={(authname) => setDraft((prev) => ({ ...prev, authname }))}
          user={localStorage.getItem("user")}
          tenant={localStorage.getItem("tenant")}
          onClose={() => {
            setSetupGuideAlert(null);
            setFieldsOpen(false);
          }}
          onSave={(nextFields) => {
            setSetupGuideAlert(null);
            const verifiedBase =
              draft.verifiedApiBase ||
              draft.apiBaseUrl ||
              draft.config?.verifiedApiBase ||
              draft.config?.apiBaseUrl ||
              verifiedBasesFromActions[0] ||
              "";
            const normalized = (nextFields || []).map((f) => {
              const coalesced = coalesceStructuredFieldOnSave(f);
              if (
                String(coalesced?.name || "").toLowerCase() === "http request" &&
                coalesced?.value &&
                typeof coalesced.value === "object" &&
                !Array.isArray(coalesced.value)
              ) {
                return {
                  ...coalesced,
                  value: normalizeCustomRequestHttpValue(coalesced.value, {
                    apiBaseUrl: verifiedBase,
                    verifiedApiBase: verifiedBase,
                    verifiedBases: verifiedBasesFromActions,
                  }),
                };
              }
              return coalesced;
            });
            const httpField = normalized.find(
              (f) => String(f?.name || "").toLowerCase() === "http request"
            );
            if (
              httpField?.value &&
              typeof httpField.value === "object" &&
              !isAbsoluteHttpUrl(httpField.value.url)
            ) {
              setSetupGuideAlert({
              title: "Full API URL required",
              body:
                "Custom Request needs a full https:// endpoint URL before saving. Paste it from the app’s API docs (or use a verified base from sibling actions).",
            });
              return;
            }
            const withMeta = {
              ...draft,
              fields: normalized,
              ...(verifiedBase
                ? {
                    verifiedApiBase: verifiedBase,
                    apiBaseUrl: verifiedBase,
                    config: {
                      ...(draft.config || {}),
                      verifiedApiBase: verifiedBase,
                      apiBaseUrl: verifiedBase,
                    },
                  }
                : {}),
            };
            setDraft(withMeta);
            const nextActions = persistTool(normalized, withMeta);
            setFieldsOpen(false);
            if (typeof onCommit === "function") onCommit(nextActions);
          }}
        />
      </CardContent>
    </Card>
  );
};

export default AgentActionsSection;
